import { apiRequest } from './apiInterceptor';
import { createQueryString } from './apiService';

const BASE_URL = '/api/flashcarddeck';

/**
 * Detects a plan-limit / quota 403 surfaced by the API interceptor.
 * The interceptor deliberately does NOT treat these as auth errors, so
 * components can catch them and render upgrade messaging instead of crashing.
 */
export const isQuotaError = (error) => {
  if (!error) return false;
  const status = error.status || (error.response && error.response.status);
  if (status !== 403) return false;
  const data = error.data || error.body || (error.response && error.response.data);
  return !!(
    data && (
      data.title === 'Plan Limit Exceeded' ||
      data.detail ||
      data.usageMetrics ||
      data.UsageMetrics ||
      data.limit !== undefined ||
      data.usage !== undefined
    )
  );
};

/**
 * Pulls the most useful human-readable message out of an API error, preferring
 * ProblemDetails' `detail` (the specific "why") over `title` (the generic
 * category) or the generic message set by the fetch wrapper. Used to surface
 * publish/unlist/relist rejection reasons (e.g. "fewer than 50 cards") as-is.
 */
export const getErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback;
  const data = error.data || error.body || (error.response && error.response.data);
  return (data && (data.detail || data.message || data.title)) || error.message || fallback;
};

const flashcardService = {
  // --- Entitlement -------------------------------------------------------
  getEntitlement: () => apiRequest(`${BASE_URL}/entitlement`, { method: 'GET' }),

  // --- Decks -------------------------------------------------------------
  getDecks: (params = {}) => {
    const queryString = createQueryString(params);
    return apiRequest(`${BASE_URL}${queryString}`, { method: 'GET' });
  },

  getDeck: (id) => apiRequest(`${BASE_URL}/${id}`, { method: 'GET' }),

  createDeck: (data) =>
    apiRequest(BASE_URL, { method: 'POST', body: JSON.stringify(data) }),

  updateDeck: (id, data) =>
    apiRequest(`${BASE_URL}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteDeck: (id) => apiRequest(`${BASE_URL}/${id}`, { method: 'DELETE' }),

  // --- Publish-as-snapshot -------------------------------------------------
  publishDeck: (id) => apiRequest(`${BASE_URL}/${id}/publish`, { method: 'POST' }),

  unlistDeck: (id) => apiRequest(`${BASE_URL}/${id}/unlist`, { method: 'POST' }),

  relistDeck: (id) => apiRequest(`${BASE_URL}/${id}/relist`, { method: 'POST' }),

  // --- Cards -------------------------------------------------------------
  getCards: (deckId) => apiRequest(`${BASE_URL}/${deckId}/cards`, { method: 'GET' }),

  createCard: (deckId, data) =>
    apiRequest(`${BASE_URL}/${deckId}/cards`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateCard: (deckId, cardId, data) =>
    apiRequest(`${BASE_URL}/${deckId}/cards/${cardId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteCard: (deckId, cardId) =>
    apiRequest(`${BASE_URL}/${deckId}/cards/${cardId}`, { method: 'DELETE' }),

  // --- Preferences (favorite / study list) -------------------------------
  setPreference: (deckId, prefs) =>
    apiRequest(`${BASE_URL}/${deckId}/preference`, {
      method: 'PUT',
      body: JSON.stringify(prefs),
    }),

  // --- Reviews -----------------------------------------------------------
  getReviews: (deckId, params = {}) => {
    const queryString = createQueryString(params);
    return apiRequest(`${BASE_URL}/${deckId}/reviews${queryString}`, { method: 'GET' });
  },

  upsertReview: (deckId, data) =>
    apiRequest(`${BASE_URL}/${deckId}/review`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteReview: (deckId) =>
    apiRequest(`${BASE_URL}/${deckId}/review`, { method: 'DELETE' }),

  // --- Stats -------------------------------------------------------------
  getDeckStats: (deckId) => apiRequest(`${BASE_URL}/${deckId}/stats`, { method: 'GET' }),

  // --- Study sessions ----------------------------------------------------
  startStudySession: (deckId) =>
    apiRequest(`${BASE_URL}/${deckId}/study/start`, { method: 'POST' }),

  submitResponse: (sessionId, data) =>
    apiRequest(`${BASE_URL}/study/${sessionId}/response`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  endStudySession: (sessionId) =>
    apiRequest(`${BASE_URL}/study/${sessionId}/end`, { method: 'PUT' }),

  // --- AI generation -----------------------------------------------------
  generateAICard: (questionId, regenerate = false) =>
    apiRequest(`${BASE_URL}/ai/generate`, {
      method: 'POST',
      body: JSON.stringify({ questionId, regenerate }),
    }),
};

export default flashcardService;
